import type { FunctionalComponent, JSX, VNode } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { storageUrl } from '@/lib/utils.ts';

/**
 * `<img>` 가 받는 속성 그대로 받는다.
 *
 * `JSX.HTMLAttributes<HTMLImageElement>` 가 아니라 `JSX.IntrinsicElements['img']`
 * 를 쓴다. 앞의 것에는 `alt` 처럼 이미지에만 있는 속성이 빠져 있다.
 */
type Props = Omit<JSX.IntrinsicElements['img'], 'src' | 'onError'> & {
  src?: string | null;
  /** 못 불러왔을 때 대신 그릴 것. 없으면 아무것도 그리지 않는다 */
  fallback?: VNode | null;
};

/**
 * 깨져도 흉하지 않은 이미지.
 *
 * DB 에서 온 주소는 언제든 없을 수 있다 — 파일이 지워졌거나, 운영 서버에만 있거나,
 * 관리자가 경로를 잘못 적었거나. 그대로 `<img>` 를 쓰면 브라우저가 **깨진 이미지
 * 아이콘**을 그려서 화면이 고장난 것처럼 보인다.
 *
 * 여기서는 못 불러오면 **조용히 감춘다.** `fallback` 을 주면 그것을 대신 그린다.
 *
 * ★ 우리가 번들에 넣어 둔 정적 아이콘(`/images/arrow-right.png` 같은 것)에는 쓰지
 *   않는다. 그건 없으면 **버그**라서 눈에 띄는 편이 낫다. 이 컴포넌트는 **데이터에서
 *   온 주소**에만 쓴다.
 */
export const SafeImage: FunctionalComponent<Props> = ({ src, fallback, ...rest }) => {
  const [failed, setFailed] = useState(false);

  /**
   * 업로드 이미지는 API 서버에 있다. 여기서 한 번에 바꿔 준다.
   *
   * 호출하는 쪽마다 감싸게 하면 언젠가 빠뜨린다 — 실제로 웹진·이벤트 이미지가
   * 그래서 엑박으로 나왔다. 이 컴포넌트는 **데이터에서 온 이미지 전용**이라
   * 여기 두는 것이 안전하다.
   */
  const resolved = storageUrl(src);

  // 주소가 바뀌면 다시 시도한다. 안 그러면 한 번 실패한 자리가 영영 비어 있다.
  useEffect(() => setFailed(false), [resolved]);

  if (!resolved || failed) return fallback ?? null;

  return <img {...rest} src={resolved} onError={() => setFailed(true)} />;
};
